import React from 'react';
import { Link } from 'react-router-dom';
import { Heart } from '@phosphor-icons/react';

import { Icon } from '../../components/Icon';
import { cn } from '../../lib/cn';
import { useViewport } from '../../hooks/useViewport';
import { ProductCard } from '../../components/mobile';
import { quickAddAccessibleName, quickAddText } from './quickAdd';

import './market.css';

/**
 * The market pattern: one listing card, one prop API, both tracks.
 *
 * WHY THIS IS ONE COMPONENT AND NOT SIX
 * -------------------------------------
 * The same card appears on the marketplace grid, a seller's store, search
 * results, the sets pages, the wishlist, and the "related" rails on a listing
 * detail. The app currently has a different implementation on most of those.
 * Adding a seventh is the failure mode this exists to prevent, so this file is
 * the only place a listing card is built.
 *
 * The mobile track already has the whole card as the `ProductCard` organism,
 * and its prop names are good, so THIS COMPONENT MIRRORS THEM EXACTLY. A page
 * writes one set of props and gets `ds-pcard` below 1024 and
 * `dsk-listing-card` at and above it. Nothing renders twice.
 *
 * ANATOMY ORDER IS FIXED, on both tracks:
 *   set kicker, card name, meta row (rarity + language + card code), price line
 *   ("N listings from" + price), market price, and availability on the tile.
 *
 * `language` was added for B3. The app's card catalogue is multi-language and
 * the marketplace has always shown it as a second pill beside the rarity, so
 * the slot is real data rather than a new idea. It is NOT `condition`: the
 * lookup-cards endpoint has no condition, condition belongs to a listing, and
 * putting a language in the condition slot would be a lie the buyer acts on.
 *
 * DOCTRINE THIS ENFORCES, none of it optional:
 * - PRICE IS LIGHTER THAN THE TITLE. Never the other way round.
 * - NO SELLER NAME ON A TILE. `seller` renders in `row` mode only, where the
 *   user is choosing between sellers rather than browsing cards.
 * - Prices are RANDS and arrive pre-formatted with the `R`. This component
 *   never formats currency, and it must never be handed a dollar amount.
 * - There are no PSA / BGS / CGC grades in this product. `condition` is real.
 * - The heart FILLS when saved. It never turns accent: a save is a command.
 *   Filled, it also STOPS being hover-only and stays on the card, because the
 *   whole point of the fill is that you can see what you have already saved
 *   without touching anything.
 * - Hover is a reveal and never the only path, so quick add and the heart are
 *   always focusable and focus brings them back on screen.
 */

/** The grid wrapper. 4 up on desktop, 2 up below the handoff. */
export function ListingGrid({ columns = 4, className, ...rest }) {
  const columnsClass = { 3: 'dsa-listing-grid--3', 4: null, 5: 'dsa-listing-grid--5' }[columns];
  return <div className={cn('dsa-listing-grid', columnsClass, className)} {...rest} />;
}

/**
 * The stacked-row wrapper: the OTHER HALF OF THE BROWSE TOGGLE, again.
 *
 * There are exactly two browse views, grid and rows, and this is rows. It was
 * briefly replaced by `ListingRail`, a horizontal rail of the same tiles, on the
 * argument that browsing is looking at art and a 44px thumbnail is not art. The
 * designer built it, saw it, and ruled the other way: a rail is one line of
 * cards you have to drag through, so it shows FEWER cards than the grid it is
 * an alternative to, and the second view is only worth having if it trades the
 * art away for something — density and a price column you can run your eye down.
 * The rail is gone rather than kept as a third option, because a browse toggle
 * with three answers is a control nobody reads.
 *
 * It is still also the right container wherever a buyer is comparing SELLERS of
 * one card, which is what it was doing while it was orphaned.
 */
export function ListingList({ className, ...rest }) {
  return <div className={cn('dsk-listing-list', className)} {...rest} />;
}

/**
 * The wishlist heart that sits on a tile. "Add to Collection".
 *
 * Rendered as a `role="button"` span, because the card around it is an `<a>`
 * and a nested interactive element is invalid there. `preventDefault` plus
 * `stopPropagation` is what keeps a save from also being a page change: the
 * anchor navigates on the default action, and `MarketplaceGrid`'s own `onClick`
 * on the card root navigates on the bubble, so stopping only one of the two
 * still lands the buyer on the card detail page.
 *
 * The label does NOT flip to "Remove from Collection" when saved, and that is
 * the ARIA rule rather than an oversight: with `aria-pressed` the name says what
 * the control is FOR and the pressed state says whether it is on. A name that
 * changes with the state gets announced as a second, different control.
 *
 * The tooltip is `data-tip` plus a CSS `::after` in market.css rather than the
 * library's `.dsk-tooltip`, which is a fixed-position layer driven by a
 * behaviour script this pattern does not mount, and rather than a native
 * `title`, which never appears for a keyboard user.
 */
function TileHeart({ pressed, label = 'Add to Collection', onToggle }) {
  function fire(event) {
    event.preventDefault();
    event.stopPropagation();
    if (onToggle) onToggle(!pressed, event);
  }

  return (
    <span
      role="button"
      tabIndex={0}
      aria-pressed={pressed}
      aria-label={label}
      data-tip={label}
      className="dsa-listing__heart"
      onClick={fire}
      onKeyDown={(event) => {
        // Space scrolls the page and Enter follows the card's own anchor, so
        // both have to be defaulted before the toggle runs.
        if (event.key === 'Enter' || event.key === ' ') fire(event);
      }}
    >
      <Icon as={Heart} size="sm" active={pressed} />
    </span>
  );
}

/** The desktop grid card. Not exported: reach it through `ListingCard`. */
function DesktopListingCard({
  href,
  onClick,
  image,
  imageAlt = '',
  set,
  name,
  rarity,
  language,
  condition,
  cardNumber,
  listingCount,
  fromLabel,
  price,
  /* A SHORT QUALIFIER ON THE PRICE, never a replacement for it -- see the
     identical prop on the mobile `ProductCard`, whose docstring carries the
     reasoning for both tracks. */
  priceNote,
  marketPrice,
  marketLabel = 'Market',
  availability,
  wishlist = false,
  wishlisted = false,
  wishlistLabel,
  onWishlistToggle,
  quickAddLabel = 'Add to cart',
  /* THE ADD IS IN FLIGHT. Same contract, same ~24s worst case, as the mobile
     card's prop of this name. */
  quickAddPending = false,
  quickAddPendingLabel = 'Confirming price…',
  inCart = false,
  onQuickAdd,
  className,
  ...rest
}) {
  const Root = href ? Link : 'div';
  const rootProps = href ? { to: href } : {};
  const quickAddWords = quickAddText({
    pending: quickAddPending,
    pendingLabel: quickAddPendingLabel,
    inCart,
    label: quickAddLabel,
  });

  return (
    <Root
      className={cn('dsk-listing-card', inCart && 'is-in-cart', className)}
      onClick={onClick}
      {...rootProps}
      {...rest}
    >
      <div className="dsk-listing-card__tile">
        {image ? <img src={image} alt={imageAlt} /> : null}
        {availability ? (
          <span className="dsa-listing__avail dsk-avail">{availability}</span>
        ) : null}
        {wishlist || onWishlistToggle ? (
          <TileHeart pressed={wishlisted} label={wishlistLabel} onToggle={onWishlistToggle} />
        ) : null}
        {inCart ? <span className="dsk-incart-flag">In cart</span> : null}
      </div>

      {set ? <div className="dsk-listing-card__kicker">{set}</div> : null}
      <div className="dsk-listing-card__title">{name}</div>

      {rarity || language || condition || cardNumber ? (
        <div className="dsa-listing__meta">
          {rarity ? <span className="dsa-listing__rarity">{rarity}</span> : null}
          {language ? <span className="dsa-listing__rarity">{language}</span> : null}
          {condition ? <span className="dsa-listing__rarity">{condition}</span> : null}
          {cardNumber ? <span className="dsa-listing__code">{cardNumber}</span> : null}
        </div>
      ) : null}

      {/* `marketPrice` counts. Browse knows only a catalog reference price and
          passes it in that slot alone; gating on `price` dropped the whole row
          and the tile showed no figure at all. */}
      {price || marketPrice || listingCount != null || priceNote ? (
        <div className="dsa-listing__pricerow">
          <span className="dsk-listing-card__price">
            {listingCount != null ? (
              <span className="dsa-listing__from">
                {listingCount} {listingCount === 1 ? 'listing' : 'listings'} from
              </span>
            ) : fromLabel ? (
              <span className="dsa-listing__from">{fromLabel}</span>
            ) : null}
            {price}
          </span>
          {/* AFTER the figure, never instead of it. Reading order is the
              qualification too: "R764.15, Price unconfirmed" is a figure with a
              caveat, where the reverse reads as a heading over a number. */}
          {priceNote ? <span className="dsa-listing__pricenote">{priceNote}</span> : null}
          {/* Only when it ADDS something. A caller with no listing minimum to
              show falls back to the reference price for both slots, and the
              tile then printed one number twice ("R1,884.81" over "Market
              R1,884.81"). Same figure, said once. */}
          {marketPrice && marketPrice !== price ? (
            /* `is-sole` when there is no buy price beside it. The green is the
               TCGplayer convention for a market price sitting BESIDE the price
               you pay, where it reads as reference. Alone on a browse tile it
               has nothing to compare against and reads as a discount, so it
               drops back to ordinary secondary text. */
            <span
              className={cn(
                'dsk-listing-card__market dsa-listing__market',
                !price && 'is-sole',
              )}
            >
              {marketLabel} {marketPrice}
            </span>
          ) : null}
        </div>
      ) : null}

      {onQuickAdd ? (
        <button
          type="button"
          className="dsa-listing__quickadd"
          /* A REAL GATE, not a label swap -- and `aria-disabled` rather than
             `disabled`, so activating it does not drop the buyer's focus to
             `<body>` for a wait that can run ~24s. See the mobile twin for the
             full reasoning, and for why this gate is additional to the hook's
             own claim rather than the guarantee. */
          aria-disabled={quickAddPending || undefined}
          aria-busy={quickAddPending || undefined}
          aria-label={quickAddAccessibleName({
            text: quickAddWords,
            note: priceNote,
            pending: quickAddPending,
          })}
          onClick={(event) => {
            /* Before the pending check: a tap on a busy button must still not
               navigate the card behind it. */
            event.preventDefault();
            event.stopPropagation();
            if (quickAddPending) return;
            onQuickAdd(event);
          }}
        >
          {quickAddWords}
        </button>
      ) : null}
    </Root>
  );
}

/** The desktop list row. Same card model, laid out horizontally. */
function DesktopListingRow({
  href,
  onClick,
  image,
  imageAlt = '',
  set,
  name,
  rarity,
  language,
  condition,
  cardNumber,
  seller,
  listingCount,
  fromLabel,
  price,
  /* The row is the SAME card model as the tile, so it carries the same
     qualifier. Losing the marker by flipping the browse toggle would make an
     honest tile a matter of which view the buyer happens to be in. */
  priceNote,
  marketPrice,
  marketLabel = 'Market',
  availability,
  wishlist = false,
  wishlisted = false,
  wishlistLabel,
  onWishlistToggle,
  actions,
  /* SWALLOWED, NOT RENDERED. The row has no quick-add control -- list view is
     for comparing, and the add lives on the card page it links to -- but the
     grid spreads one prop set across both modes, so without naming these four
     they fall through `...rest` onto the root element as DOM attributes and
     React warns about every one of them. The tile above is where they mean
     something. */
  onQuickAdd,
  quickAddLabel,
  quickAddPending,
  quickAddPendingLabel,
  inCart,
  className,
  ...rest
}) {
  const Root = href ? Link : 'div';
  const rootProps = href ? { to: href } : {};
  // Same rule as the tile: the heart is on every surface that lists cards, so
  // switching to rows must not be a way to lose it.
  const heart = wishlist || onWishlistToggle;

  return (
    <Root className={cn('dsk-listing-row', className)} onClick={onClick} {...rootProps} {...rest}>
      {/* The row is the SAME card model as the tile, so it shows the same art.
          It rendered an empty box instead: `image`/`imageAlt` were never
          destructured here, so they fell through `...rest` onto the root
          element as DOM attributes and list view showed a gradient placeholder
          for every card while grid view showed the scan. Sizing is in
          app-layer.css — a 44px thumbnail fills, it does not letterbox. */}
      <div className="dsk-listing-row__tile">
        {image ? <img src={image} alt={imageAlt} /> : null}
      </div>

      <div className="dsk-listing-row__main">
        {set ? <span className="dsk-listing-row__kicker">{set}</span> : null}
        <span className="dsk-listing-row__title">{name}</span>
        {availability || rarity || language || condition || cardNumber || seller ? (
          <div className="dsk-listing-row__meta">
            {/* Leads the line, and is the same badge the tile floats over the
                art — `availability` was not destructured here, so it landed on
                the root element as a DOM attribute and list view dropped the
                count entirely. A card with one listing looked identical to one
                with twelve. */}
            {availability ? (
              <span className="dsa-listing__availrow dsk-avail">{availability}</span>
            ) : null}
            {rarity ? <span className="dsa-listing__rarity">{rarity}</span> : null}
            {language ? <span className="dsa-listing__rarity">{language}</span> : null}
            {condition ? <span className="dsa-listing__rarity">{condition}</span> : null}
            {cardNumber ? <span className="dsa-listing__code">{cardNumber}</span> : null}
            {seller ? <span className="dsa-listing__code">{seller}</span> : null}
          </div>
        ) : null}
      </div>

      <div className="dsk-listing-row__prices">
        {listingCount != null ? (
          <span className="dsk-listing-row__kicker">
            {listingCount} {listingCount === 1 ? 'listing' : 'listings'} from
          </span>
        ) : fromLabel ? (
          <span className="dsk-listing-row__kicker">{fromLabel}</span>
        ) : null}
        {price ? <div className="dsk-listing-row__price">{price}</div> : null}
        {priceNote ? <div className="dsa-listing__pricenote">{priceNote}</div> : null}
        {/* Same rule as the tile: never print the same figure twice. */}
        {marketPrice && marketPrice !== price ? (
          <div className="dsk-listing-row__market">
            {marketLabel} {marketPrice}
          </div>
        ) : null}
      </div>

      {actions || heart ? (
        <div className="dsk-listing-row__actions">
          {actions}
          {/* `.dsa-listing__heart` is absolute against the tile on a card; the
              row's actions column is not a positioning context, so the row
              variant drops it back into flow. Same fix, same reason, as
              `.ds-pcard--row .ds-heart` in mobile/organisms.css. */}
          {heart ? (
            <span className="dsa-listing__heartslot">
              <TileHeart
                pressed={wishlisted}
                label={wishlistLabel}
                onToggle={onWishlistToggle}
              />
            </span>
          ) : null}
        </div>
      ) : null}
    </Root>
  );
}

/**
 * The listing card. `mode="tile"` is the grid unit, `mode="row"` is list view.
 *
 * Below 1024 both modes are the mobile `ProductCard`, which already models
 * tile, row and compact. At and above 1024 they are the desktop card and row.
 */
export function ListingCard({ mode = 'tile', ...props }) {
  const { isDesktop } = useViewport();

  if (!isDesktop) {
    return <ProductCard mode={mode} {...props} />;
  }

  return mode === 'row' ? <DesktopListingRow {...props} /> : <DesktopListingCard {...props} />;
}

export default ListingCard;
